import type {
  LibraryTemplate,
  CreateTemplateRequestBody,
} from "@/common/types/templates";
import type { TemplateFormSchemaType } from "@/schemas/template.schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Copy, Eye } from "lucide-react";
import { useState, useEffect } from "react";
import { useTemplateStore } from "@/store/templates.store";
import { useAuthStore } from "@/store/auth.store";
import { toast } from "@/hooks/use-toast";
import { TemplateForm } from "@/components/ui/templates/template-form";
import { useCreateTemplate } from "@/hooks/templates/useCreateTemplate";
import { FileResultRendererMD } from "@/components/ui/visits/file-result-renderer-md";
import { useNavigate } from "@tanstack/react-router";

export const LibraryItem = ({
  template,
}: {
  template: LibraryTemplate;
}): JSX.Element => {
  const { t } = useTranslation();
  const { fetchAndSetTemplates } = useTemplateStore();
  const { userData } = useAuthStore.getState();
  const [isViewing, setIsViewing] = useState(false);
  const [isCopyingDialogOpen, setIsCopyingDialogOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [copiedTemplateData, setCopiedTemplateData] =
    useState<TemplateFormSchemaType | null>(null);
  const { createTemplate, error, result, loading } = useCreateTemplate();
  const navigate = useNavigate();

  // Convert LibraryTemplate to Template-like object for the form
  // Create a fresh copy of headers array to ensure form updates properly
  const templateForForm = {
    id: 0,
    name: template.name,
    createDate: "",
    userId: userData?.id || 0,
    headers: [...(template.headers || [])],
    optionalText: template.optionalText || "",
    isDefault: 0,
  };

  const handleCopyTemplate = (formData: TemplateFormSchemaType): void => {
    if (!userData?.id) {
      toast({
        variant: "error",
        title: t("templates.authError.title"),
        description: t("templates.authError.description"),
      });
      return;
    }

    setHasSubmitted(true);
    setCopiedTemplateData(formData);
    const body: CreateTemplateRequestBody = {
      userId: userData.id,
      name: formData.name,
      headers: formData.headers,
      optionalText: formData.optionalText || "",
      isDefault: formData.isDefault,
    };

    void createTemplate(body);
  };

  // Reset submission tracking when dialog closes
  useEffect(() => {
    if (!isCopyingDialogOpen) {
      setHasSubmitted(false);
    }
  }, [isCopyingDialogOpen]);

  useEffect(() => {
    // Only show toast if we actually submitted and dialog was open
    if (hasSubmitted && result != null && !loading && isCopyingDialogOpen) {
      if (result) {
        setIsCopyingDialogOpen(false);
        // Refresh user templates after successful copy
        if (userData?.id) {
          void fetchAndSetTemplates({ userId: userData.id }).then(() => {
            // Find the newly created template by matching name and headers
            // We use a small delay to ensure templates are updated
            setTimeout(() => {
              const { templates: updatedTemplates } =
                useTemplateStore.getState();
              if (!copiedTemplateData) {
                return;
              }

              const sortedCopiedHeaders = [...copiedTemplateData.headers].sort(
                (a, b) => a.localeCompare(b)
              );
              const newTemplate = updatedTemplates.find((t) => {
                const sortedTemplateHeaders = [...t.headers].sort((a, b) =>
                  a.localeCompare(b)
                );
                return (
                  t.name === copiedTemplateData.name &&
                  JSON.stringify(sortedTemplateHeaders) ===
                    JSON.stringify(sortedCopiedHeaders)
                );
              });

              if (newTemplate) {
                void navigate({ to: `/template/${newTemplate.id}` });
                toast({
                  variant: "default",
                  title: t("templates.copySuccess"),
                  description: `"${template.name}" has been copied to your templates`,
                });
              } else {
                // This is a fallback - template exists but matching failed
                toast({
                  variant: "default",
                  title: t("templates.copySuccess"),
                  description: `"${template.name}" has been copied. Please check "My Templates" to edit it.`,
                });
              }
            }, 300);
          });
        }
      }
    }

    if (
      hasSubmitted &&
      error &&
      result != null &&
      !loading &&
      isCopyingDialogOpen
    ) {
      toast({
        variant: "error",
        title: t("templates.copyError.title"),
        description: t("templates.copyError.description"),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    result,
    loading,
    error,
    hasSubmitted,
    isCopyingDialogOpen,
    copiedTemplateData,
  ]);

  const handleViewTemplate = (): void => {
    setIsViewing(true);
  };

  return (
    <>
      <div className="flex items-center justify-between py-4 px-6">
        <div className="space-y-1">
          <h3 className="text-primary font-medium text-base">
            {template.name}
          </h3>
          {template.optionalText && (
            <p className="text-sm text-grey-400 line-clamp-2">
              {template.optionalText.length > 150
                ? `${template.optionalText.substring(0, 150)}...`
                : template.optionalText}
            </p>
          )}
          <div className="text-xs text-grey-500">
            {template.headers.length} sections
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            className="flex items-center space-x-1"
            size="sm"
            variant="outline"
            onClick={handleViewTemplate}
          >
            <Eye className="w-4 h-4" />
            <span>{t("templates.viewTemplate")}</span>
          </Button>

          <Button
            className="flex items-center space-x-1"
            disabled={loading}
            size="sm"
            variant="default"
            onClick={() => {
              setIsCopyingDialogOpen(true);
            }}
          >
            <Copy className="w-4 h-4" />
            <span>
              {loading
                ? t("templates.copyTemplateDialog.copyingButton")
                : t("templates.copyToMyTemplates")}
            </span>
          </Button>
        </div>
      </div>

      {/* Copy Template Dialog with Edit Form */}
      <Dialog open={isCopyingDialogOpen} onOpenChange={setIsCopyingDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("templates.copyTemplateDialog.title")}</DialogTitle>
          </DialogHeader>
          <TemplateForm
            key={`${template.id}-${isCopyingDialogOpen}`}
            template={templateForForm}
            submitText={
              loading
                ? t("templates.copyTemplateDialog.copyingButton")
                : t("templates.copyTemplateDialog.copyButton")
            }
            onSubmit={handleCopyTemplate}
            onCancel={() => {
              setIsCopyingDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Template Details Modal */}
      <AlertDialog open={isViewing} onOpenChange={setIsViewing}>
        <AlertDialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-semibold">
              {template.name}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-6 mt-4">
            {/* Template Sections */}
            <div>
              <h4 className="font-semibold text-base mb-3 text-grey-800">
                {t("templates.templateDetails.sections")}
              </h4>
              <div className="flex flex-wrap gap-2">
                {template.headers.map((header) => (
                  <span
                    key={header}
                    className="inline-flex items-center bg-primary/10 text-primary px-3 py-1 rounded-md text-sm font-medium"
                  >
                    {header}
                  </span>
                ))}
              </div>
            </div>

            {/* Full Instructions */}
            {template.optionalText && (
              <div>
                <h4 className="font-semibold text-base mb-3 text-grey-800">
                  Template Instructions
                </h4>
                <div className="bg-grey-50 rounded-lg p-4 border border-grey-200">
                  <div className="prose prose-sm max-w-none">
                    <FileResultRendererMD result={template.optionalText} />
                  </div>
                </div>
              </div>
            )}

            {!template.optionalText && (
              <div className="text-sm text-grey-500 italic">
                {t("templates.templateDetails.details")}
              </div>
            )}
          </div>
          <AlertDialogFooter className="mt-6">
            <AlertDialogAction
              onClick={() => {
                setIsViewing(false);
              }}
            >
              {t("templates.templateDetails.closeButton")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
